import { Icon } from '@cx/ui';
import Tippy from '@tippyjs/react';
import { SortingTypeValue } from 'helpers/DataGridSorting';
import { HiArrowSmRight, HiArrowSmUp, HiOutlineArrowSmDown } from 'react-icons/hi';
import { TableType } from 'models/WidgetModel';

export const getIsPercentageFromRow = (row: any, field: any) => {
  const fieldPercent = row[field];
  if (
    fieldPercent &&
    typeof fieldPercent === 'object' &&
    'isPercentage' in fieldPercent &&
    field != 'field_1'
  ) {
    return fieldPercent.isPercentage == true ? fieldPercent.isPercentage : false;
  } else {
    return false;
  }
};

// Get Trend Subcomponent based on significanceIndicator
// This is used to show the significance indicator in the table
export const getSignificanceValueFromRow = (row: any, field: string) => {
  const fieldV = row[field];
  if (fieldV && typeof fieldV === 'object' && 'significanceIndicator' in fieldV) {
    const trendup = (
      <div className="pck-child-div significanceIndicator">
        <Icon name="metricArrowUp" className="biggest-green-arrow "></Icon>
      </div>
    );
    const trenddown = (
      <div className="pck-child-div significanceIndicator">
        <Icon name="metricArrowDown" className="biggest-red-arrow"></Icon>
      </div>
    );
    const trendflat = (
      <div className="pck-child-div significanceIndicator">
        <Icon name="metricArrowRight" className="biggest-yellow-arrow"></Icon>
      </div>
    );
    if (fieldV.significanceIndicator && fieldV.valueDifference > 0) return trendup;
    else if (fieldV.significanceIndicator && fieldV.valueDifference < 0) return trenddown;
    else if (fieldV.significanceIndicator && fieldV.valueDifference == 0) return trendflat;
  }
  return <></>;
};

// Get indicator based on current and previous value
export const getComparisonValueFromRow = (
  row: any,
  field: string,
  prevField: string,
  decimalPlaces?: number | string | null
) => {
  if (!row || !field || !prevField) {
    return <></>;
  }

  const currentRawValue = row[field]?.value ?? row[field];
  const prevRawValue = row[prevField]?.value ?? row[prevField];
  const currentValue = Number(currentRawValue);
  const prevValue = Number(prevRawValue);

  // Skip non Numeric
  if (Number.isNaN(currentValue) || Number.isNaN(prevValue)) {
    return <></>;
  }

  const decimals = Number(decimalPlaces);
  const hasValidDecimalPlaces = Number.isInteger(decimals) && decimals >= 0;
  const valueDifference = currentValue - prevValue;
  const formattedDifference = hasValidDecimalPlaces
    ? valueDifference.toFixed(decimals)
    : valueDifference.toFixed(2);

  const trendup = (
    <Tippy
      content={
        <div title="">
          <div className="tooltip-title">{formattedDifference}</div>
        </div>
      }
    >
      <div className="pck-child-div significanceIndicator">
        <HiArrowSmUp className="biggest-green-arrow" />
      </div>
    </Tippy>
  );
  const trenddown = (
    <Tippy
      content={
        <div title="">
          <div className="tooltip-title">{formattedDifference}</div>
        </div>
      }
    >
      <div className="pck-child-div significanceIndicator">
        <HiOutlineArrowSmDown className="biggest-red-arrow" />
      </div>
    </Tippy>
  );
  const trendflat = (
    <div className="pck-child-div significanceIndicator">
      <HiArrowSmRight className="biggest-yellow-arrow" />
    </div>
  );

  if (currentValue > prevValue) return trendup;
  if (currentValue < prevValue) return trenddown;
  return trendflat;
};

//  Used only for Enterprise Dashboards, to make sure there is a relation between
//  the L1 category parents and the children.
//  TODO: we may include this info on the mongoDB queries in the future.
export const prepareEnterpriseData = (data: any) => {
  let workspaceId = 0;
  for (const item of data) {
    if (item.rowType == 'L1') {
      workspaceId = item.id;
    } else if (item.rowType == 'L2') {
      item.workspaceId = workspaceId;
    }
  }
};

export const calculateColumnWidth = (col: any, data: any[], type: SortingTypeValue): string => {
  if (!data || data.length === 0) return 'auto';

  let maxLength = 0;

  for (const row of data) {
    const cell = row[col.field];
    if (!cell) continue;

    const raw = cell?.value ?? cell;
    const length = raw?.toString?.().length ?? 0;
    const extra = cell?.significanceIndicator ? 5 : 0;

    maxLength = Math.max(maxLength, length + extra);
  }

  maxLength = Math.max(maxLength, col.displayName.length);

  if (type === TableType.ENTERPRISE) {
    maxLength += 5;
  }

  const width = Math.max(5, maxLength / 2 + 1);

  return `${width}em`;
};
